import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Mail, Star } from 'lucide-react';
import { IoLogoWhatsapp } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { templatesAPI } from '../../services/api';
import CreateTemplateModal from './CreateTemplateModal';

const TEMPLATE_CAP = 3;
const MODULES = ['CRM', 'ATS', 'INVOICE'];
const MODULE_LABELS = { CRM: 'CRM', ATS: 'ATS', INVOICE: 'Invoice' };

const TemplateLibrary = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeChannel, setActiveChannel] = useState('EMAIL');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [createDefaults, setCreateDefaults] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    seedAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seedAndFetch = async () => {
    try {
      await templatesAPI.seedTemplates();
    } catch {
      // seed already done — not an error
    }
    fetchTemplates();
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await templatesAPI.getTemplates();
      setTemplates(res.data || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (tpl.isDefault) {
      toast.error('Default templates cannot be deleted. You can edit them.');
      return;
    }
    if (!window.confirm(`Delete "${tpl.name}"?`)) return;
    setDeletingId(tpl.id);
    try {
      await templatesAPI.deleteTemplate(tpl.id);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete template');
    } finally {
      setDeletingId(null);
    }
  };

  const openCreate = (channel, mod) => {
    setCreateDefaults({ channel, module: mod });
    setEditTemplate(null);
    setModalOpen(true);
  };

  const openEdit = (tpl) => {
    setEditTemplate(tpl);
    setCreateDefaults(null);
    setModalOpen(true);
  };

  const filtered = templates.filter((t) => t.channel === activeChannel);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900">Template Library</h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage email and WhatsApp message templates for your organisation.
          Maximum {TEMPLATE_CAP} templates per channel per module.
        </p>
      </div>

      {/* Channel tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        {[
          { key: 'EMAIL',    label: 'Email',    icon: <Mail size={14} /> },
          { key: 'WHATSAPP', label: 'WhatsApp', icon: <IoLogoWhatsapp size={14} /> },
        ].map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setActiveChannel(key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeChannel === key
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {icon} {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading templates…</div>
      ) : (
        <div className="space-y-8">
          {MODULES.map((mod) => {
            const modTemplates = filtered.filter((t) => t.module === mod);
            const count = modTemplates.length;
            const atCap = count >= TEMPLATE_CAP;
            return (
              <div key={mod}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
                      {MODULE_LABELS[mod]}
                    </h2>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        atCap ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {count}/{TEMPLATE_CAP}
                    </span>
                  </div>
                  {!atCap && (
                    <button
                      onClick={() => openCreate(activeChannel, mod)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus size={12} /> Add Template
                    </button>
                  )}
                </div>

                {modTemplates.length === 0 ? (
                  <div className="text-sm text-slate-400 py-3 px-4 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    No templates yet. Click "Add Template" to create one.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {modTemplates.map((tpl) => (
                      <div
                        key={tpl.id}
                        className="flex items-start justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
                      >
                        <div className="flex-1 min-w-0 pr-4">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-semibold text-slate-800">{tpl.name}</p>
                            {tpl.isDefault && (
                              <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded">
                                <Star size={10} /> Default
                              </span>
                            )}
                          </div>
                          {tpl.subject && (
                            <p className="text-xs text-slate-500 mb-1 truncate">
                              Subject: {tpl.subject}
                            </p>
                          )}
                          <p className="text-xs text-slate-400 line-clamp-2">{tpl.body}</p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0 mt-0.5">
                          <button
                            onClick={() => openEdit(tpl)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(tpl)}
                            disabled={deletingId === tpl.id || tpl.isDefault}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={tpl.isDefault ? 'Default templates cannot be deleted' : 'Delete'}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CreateTemplateModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchTemplates}
        editTemplate={editTemplate}
        defaults={createDefaults}
      />
    </div>
  );
};

export default TemplateLibrary;
